import React, { useEffect, useRef } from "react";
import {
  companyLogo,
  companyLogoOriginal,
  imgHomgBg,
  imgService2,
  imgService6,
} from "../../assets/imgExport";
import { useDispatch } from "react-redux";
import { setImgSrc } from "../../features/app/layoutsSlice";
import {
  IconChevronRight,
  IconEngineering,
  IconEnterprise,
  IconShip,
  IconStrategy,
  IconTool,
  IconWindTurbine,
} from "../../assets/svgExport";
import { NavLink } from "react-router-dom";
import GetInTouch from "../../components/GetInTouch/GetInTouch";

export default function HomePage() {
  const dispatch = useDispatch();

  const heroSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const heroSectionHeight = heroSectionRef.current?.offsetHeight || 0;
      if (window.scrollY > heroSectionHeight) {
        dispatch(setImgSrc(companyLogoOriginal));
      } else {
        dispatch(setImgSrc(companyLogo));
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch]);

  function HeroSection() {
    return (
      <div
        ref={heroSectionRef}
        className="relative flex h-screen min-h-[800px] flex-col px-10 sm:px-20 pt-20 justify-center items-center text-white"
      >
        <div className="relative z-10">
          <h1 className="mt-4 text-inherit flex flex-col gap-4 max-w-[700px]">
            Supporting ambitious European offshore wind projects with shipping
            success
          </h1>
          <p className="text-sm text-white mt-10 w-full max-w-[1200px] leading-relaxed ">
            Through direct partnerships with Asia's premier vessel operators, we
            match you with the right ships and crews for your cargo. From there,
            we can manage logistics to ensure your components arrive
            installation-ready at pricing that works for your project.
          </p>

          <NavLink
            className="text-sm text-main-dark inline-block mt-10 bg-white border border-white px-6 py-2 hover:border-main-dark"
            to="/services"
          >
            How we help
          </NavLink>
        </div>
        <div className="absolute top-0 h-full w-full">
          <img
            className="h-full w-full object-cover"
            src={imgHomgBg}
            alt={
              "an offshore wind farm at sunrise, symbolizing the projects we empower through our marine transport services"
            }
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080F3A]/80  to-[#05726B]/80" />
          {/* <div className="absolute inset-0  bg-gradient-to-r from-main/80  to-secondary-main/80" /> */}
          {/* <div className="absolute inset-0  bg-gradient-to-r from-black/80 via-black/60 to-black/40" /> */}
        </div>
      </div>
    );
  }

  function IntroSection() {
    return (
      <div className=" equal-flex">
        <div className="">
          <p className="subtitle">The challenge</p>
          <h2>
            Transportation constraints are limiting offshore wind project
            economics.
          </h2>
          <div>
            <p className="">
              Your transportation strategy shapes a projects viability. While
              Asia offers proven heavy-lift capacity at competitive rates,
              developers face valid concerns about standards, availability, and
              risk management.
            </p>
            <p>The questions we hear most are:</p>
            <ul className="list-disc list-inside">
              <li>
                How do we access competitive rates and maintain standards?
              </li>
              <li>Can we secure vessels when we need them?</li>
              <li>Will contracting work within our procurement frameworks?</li>
              <li>
                Can we get technical engineering expertise along side shipping?
              </li>
              <li>Who manages the cultural and commercial complexity?</li>
            </ul>
            <p>Venti Maris aims to help you with all of them.</p>
          </div>
        </div>
        <img src={imgService2} alt="wind-farm-construction" />
      </div>
    );
  }

  function OurSolutionSection() {
    const data = [
      {
        title: "Optimised Planning & Chartering",
        icon: <IconStrategy width={48} height={48} />,
        description: (
          <p>
            Sourcing the right vessels, frameworks and schedules to deliver
            cost-effective transport on familiar contractual terms, without
            hidden surprises.
          </p>
        ),
      },
      {
        title: "Advanced Engineering Solutions",
        icon: <IconEngineering width={48} height={48} />,
        description: (
          <p>
            We provide all the necessary engineering and technical support to
            ensure your cargo arrives at its destination as expected through our
            engineering team.
          </p>
        ),
      },
      {
        title: "Seamless Execution",
        icon: <IconEnterprise width={48} height={48} />,
        description: (
          <p>
            Managing logistics and administration in getting your cargo moved
            across the globe so your team can focus on what they do best.
          </p>
        ),
      },
    ];

    return (
      <div className=" text-center">
        <p className="subtitle">Our solution</p>
        <h2>
          A single partnership connecting
          <br />
          Europe and Asia
        </h2>
        <p className="max-w-[70%] mx-auto">
          We combine direct relationships with Asia’s most advanced vessel
          owners and decades of European developer-side experience to deliver
          fully assured, end-to-end marine transport solutions for offshore wind
          projects.
        </p>
        <p className="font-bold">Our three core services:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10 mt-28">
          {data.map((i, index) => {
            return (
              <div key={index} className="mb-10 flex flex-col items-center">
                {i.icon}
                <h3 className="text-xl font-semibold mb-0 mt-6">{i.title}</h3>
                <p className="text-gray-700 text-sm leading-6">
                  {i.description}
                </p>
              </div>
            );
          })}
        </div>
        <NavLink
          className="inline-block mt-0 bg-transparent border border-black/80 px-10 py-4 hover:border-white/50"
          to="/services"
        >
          More service information
        </NavLink>
      </div>
    );
  }

  function OurKeyDifferentiatorsSection() {
    return (
      <div className=" equal-flex">
        <div>
          <p className="subtitle">Our key differentiators</p>
          <h2>Why choose Venti Maris</h2>
          <p>
            Together, we deliver direct access to Asia's most state-of-the-art
            fleet of heavy transport vessels. Wrapped in familiar European
            project delivery standards and backed by proven engineering
            capabilities in offshore wind foundation design.{" "}
          </p>
          <ul className="list-disc list-inside flex flex-col gap-6">
            <li>
              Direct vessel access across Asia’s heavy-transport fleet, without
              intermediaries. This gives our clients greater optionality and
              highly competitive rates for their shipping needs.
            </li>
            <li>
              Over twenty years of European developer experience, giving us a
              deep understanding of project realities, procurement frameworks
              and risk expectations.
            </li>
            <li>
              Engineering assurance through our partner Empire Engineering —
              recognised leaders in offshore engineering. Allowing us to bridge
              the gap between shipbroking and full end-to-end project execution.
            </li>
          </ul>

          <NavLink
            to="/contact"
            className="mt-10 flex items-center gap-1 text-sm"
          >
            Talk to us about your project
            <IconChevronRight className="w-4" />
          </NavLink>
        </div>
        <img src={imgService6} alt="wind-farm-shipping" />
      </div>
    );
  }

  return (
    <div>
      <HeroSection />
      <div className="body-container ">
        <IntroSection />
        <OurSolutionSection />
        <OurKeyDifferentiatorsSection />
        <GetInTouch />
      </div>
    </div>
  );
}

export function CallToActionSection() {
  return (
    <div className="bg-main/10 py-16 px-12 text-center flex flex-col items-center">
      <p className="text-2xl font-bold text-main mb-4 font-main-sans">
        Ready to simplify your offshore wind logistics?
      </p>
      <p className="text-lg text-secondary-dark mb-6">
        <span className=" text-secondary-main">Contact us today</span> to
        discover how Venti Maris can deliver your project components safely, on
        time, and on budget. Let’s build the future of renewable energy
        together.
      </p>
      <NavLink to="/contact">
        <button className="mt-4 bg-main text-white py-2 px-6 rounded-full hover:bg-secondary-main transition duration-300">
          Get in Touch
        </button>
      </NavLink>
    </div>
  );
}
